import { BaselineAudit_Entity } from '../baseline-audit';
import { ArrayTypeSchema, EnumBaselineProp, SchemaValue } from '../schema';
import { Baseline } from '../../../../stores/client/sway/baseline/model';

export interface SafeLinksPolicy_SchemaData {
    Identity: SchemaValue<string>;
    Priority: SchemaValue<number, 'number'>;
    EnableSafeLinksForEmail: SchemaValue<boolean, 'boolean'>;
    EnableForInternalSenders?: SchemaValue<boolean, 'boolean'>;
    ScanUrls?: SchemaValue<boolean, 'boolean'>;
    DeliverMessageAfterScan?: SchemaValue<boolean, 'boolean'>;
    DoNotRewriteUrls: EnumBaselineProp | { type: 'null' };
    DisableUrlRewrite?: SchemaValue<boolean, 'boolean'>;
    EnableSafeLinksForTeams: SchemaValue<boolean, 'boolean'>;
    EnableSafeLinksForOffice: SchemaValue<boolean, 'boolean'>;
    TrackClicks: SchemaValue<boolean, 'boolean'>;
    AllowClickThrough?: SchemaValue<boolean, 'boolean'>;
    EnableOrganizationBranding?: SchemaValue<boolean, 'boolean'>;
    EnableMicrosoftTranslator?: SchemaValue<boolean, 'boolean'>;
    CustomNotificationText?: SchemaValue<string>;
    SentTo?: EnumBaselineProp | { type: 'null' };
    SentToMemberOf?: EnumBaselineProp | { type: 'null' };
    RecipientDomainIs?: EnumBaselineProp | { type: 'null' };
    ExceptIfSentTo?: EnumBaselineProp | { type: 'null' };
    ExceptIfSentToMemberOf?: EnumBaselineProp | { type: 'null' };
    ExceptIfRecipientDomainIs?: EnumBaselineProp | { type: 'null' };
}

export type SafeLinksPolicy_Schema = ArrayTypeSchema<SafeLinksPolicy_SchemaData>;
export type SafeLinksPolicy_Baseline = Baseline<SafeLinksPolicy_SchemaData>;
export type SafeLinksPolicy_Deviation = BaselineAudit_Entity<SafeLinksPolicy_Schema>;

interface SafeLinksPolicy_Form {
    Identity: string;
    Priority: number;
    EnableSafeLinksForEmail: boolean;
    EnableForInternalSenders: boolean;
    ScanUrls: boolean;
    DeliverMessageAfterScan: boolean;
    DoNotRewriteUrls: string[];
    DisableUrlRewrite: boolean;
    EnableSafeLinksForTeams: boolean;
    EnableSafeLinksForOffice: boolean;
    TrackClicks: boolean;
    AllowClickThrough: boolean;
    EnableOrganizationBranding: boolean;
    useCustomNotification: boolean;
    EnableMicrosoftTranslator: boolean;
    CustomNotificationText: string;
    SentTo: string[];
    SentToMemberOf: string[];
    RecipientDomainIs: string[];
    ExceptIfSentTo: string[];
    ExceptIfSentToMemberOf: string[];
    ExceptIfRecipientDomainIs: string[];
}

export function buildSafeLinksPolicySchema(form: SafeLinksPolicy_Form): SafeLinksPolicy_Schema {
    const schema: SafeLinksPolicy_Schema = {
        type: 'array',
        contains: {
            type: 'object',
            properties: {
                Identity: { type: 'string', const: form.Identity },
                Priority: { type: 'number', const: form.Priority },
                EnableSafeLinksForEmail: { type: 'boolean', const: form.EnableSafeLinksForEmail },
                DoNotRewriteUrls: getEnumItem(form.DoNotRewriteUrls),
                EnableSafeLinksForTeams: { type: 'boolean', const: form.EnableSafeLinksForTeams },
                EnableSafeLinksForOffice: { type: 'boolean', const: form.EnableSafeLinksForOffice },
                TrackClicks: { type: 'boolean', const: form.TrackClicks },

                EnableMicrosoftTranslator: {
                    type: 'boolean',
                    const: form.useCustomNotification ? form.EnableMicrosoftTranslator : false,
                },
                CustomNotificationText: {
                    type: 'string',
                    const: form.useCustomNotification ? form.CustomNotificationText : '',
                },
                SentTo: getEnumItemOrNull(form.SentTo),
                SentToMemberOf: getEnumItemOrNull(form.SentToMemberOf),
                RecipientDomainIs: getEnumItemOrNull(form.RecipientDomainIs),
                ExceptIfSentTo: getEnumItemOrNull(form.ExceptIfSentTo),
                ExceptIfSentToMemberOf: getEnumItemOrNull(form.ExceptIfSentToMemberOf),
                ExceptIfRecipientDomainIs: getEnumItemOrNull(form.ExceptIfRecipientDomainIs),
            },
            required: ['Identity', 'Name', 'Priority'],
        },
    };

    if (form.TrackClicks) {
        schema.contains.properties.AllowClickThrough = { type: 'boolean', const: form.AllowClickThrough };
        schema.contains.properties.EnableOrganizationBranding = {
            type: 'boolean',
            const: form.EnableOrganizationBranding,
        };
    }

    if (form.EnableSafeLinksForEmail) {
        schema.contains.properties.EnableForInternalSenders = { type: 'boolean', const: form.EnableForInternalSenders };
        schema.contains.properties.ScanUrls = { type: 'boolean', const: form.ScanUrls };
        schema.contains.properties.DisableUrlRewrite = { type: 'boolean', const: form.DisableUrlRewrite };
        if (form.ScanUrls) {
            schema.contains.properties.DeliverMessageAfterScan = {
                type: 'boolean',
                const: form.DeliverMessageAfterScan,
            };
        }
    }

    return schema;
}

function getEnumItemOrNull(items: string[]): EnumBaselineProp | { type: 'null' } {
    if (items?.length > 0) {
        return {
            type: 'array',
            items: { enum: [...items], type: 'string' },
            minItems: items.length,
            maxItems: items.length,
            uniqueItems: true,
        };
    }
    return { type: 'null' };
}

function getEnumItem(items: string[]): EnumBaselineProp | { type: 'null' } {
    if (items?.length > 0) {
        return {
            type: 'array',
            items: { enum: [...items], type: 'string' },
            minItems: items.length,
            maxItems: items.length,
            uniqueItems: true,
        };
    }
    return { type: 'array', minItems: 0, maxItems: 0 };
}
